import * as React from 'react';
import { Avatar, Button, Card, Col, Dropdown, Empty, Progress, Row, Select, Spin, Table, Tabs, Typography, notification } from 'antd';
import { useParams } from 'react-router-dom';
import { Line } from '@ant-design/plots';
import {
    ReportTokenContext, useActiveReport, useCampaignAvatarMap, useCampaignQuery, useReportContentTypes, useReportHeatmap,
    useReportInfluencers, useReportLeaderboard,
    useReportSummary, useReportTopPosts, useSetReportShare,
} from '../queryHooks';
import { formatCompact, formatPct, PLATFORM_LABEL, platformColor } from '../format';
import SummarySection, { dailyChartSeries, dayAxisLabel } from '../sections/SummarySection';
import PostCard from '../components/PostCard';
import { useContext } from 'react';
import { useSuppressResizeObserverLoop } from '../useSuppressResizeObserverLoop';

const ReportView: React.FC = () => {
    const token = useContext(ReportTokenContext);
    useSuppressResizeObserverLoop();
    const { id, reportId } = useParams<{ id: string; reportId: string }>();
    const campaignId = id ? Number(id) : undefined;
    const { data: campaign } = useCampaignQuery(token ? undefined : campaignId);
    const { data: report, isLoading } = useActiveReport(campaignId, reportId);
    const share = useSetReportShare();
    const [tab, setTab] = React.useState('summary');

    const copyLink = (shareToken: string) => {
        const url = `${window.location.origin}/share/reports/${shareToken}`;
        navigator.clipboard?.writeText(url).then(
            () => notification.success({ message: 'Link copied. Anyone with it can view this report.' }),
            () => notification.info({ message: 'Share link', description: url, duration: 0 }),
        );
    };

    const enableShare = async () => {
        if (!campaignId || !reportId) return;
        try {
            const res = await share.mutateAsync({ campaignId, reportId, enabled: true });
            if (res.share_token) copyLink(res.share_token);
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not share this report' });
        }
    };

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
    if (!report) return <Empty description="Report not found" />;

    const period = { start: report.start_date, end: report.end_date };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div>
                    <Typography.Title level={3} style={{ marginTop: 0 }}>{report.name}</Typography.Title>
                    <Typography.Text type="secondary">
                        {(report.creator_count ?? campaign?.creator_count ?? 0)} Influencers
                        {report.last_refreshed_at ? ` · Last refreshed ${new Date(report.last_refreshed_at).toLocaleDateString()}` : ''}
                    </Typography.Text>
                </div>
                {!token && (report.share_token ? (
                    <Dropdown
                        menu={{
                            items: [
                                { key: 'copy', label: 'Copy link' },
                                { key: 'stop', label: 'Stop sharing' },
                            ],
                            onClick: ({ key }) => {
                                if (key === 'copy' && report.share_token) copyLink(report.share_token);
                                if (key === 'stop' && campaignId && reportId) {
                                    share.mutate({ campaignId, reportId, enabled: false });
                                }
                            },
                        }}
                    >
                        <Button loading={share.isLoading}>Shared</Button>
                    </Dropdown>
                ) : (
                    <Button loading={share.isLoading} onClick={enableShare}>Share</Button>
                ))}
            </div>
            <Tabs
                activeKey={tab}
                onChange={setTab}
                style={{ marginTop: 12 }}
                items={[
                    { key: 'summary', label: 'Summary', children: <ReportSummaryTab campaignId={campaignId} reportId={reportId} period={period} /> },
                    { key: 'leaderboard', label: 'Leaderboard', children: <LeaderboardTab campaignId={campaignId} reportId={reportId} /> },
                    { key: 'performance', label: 'Performance', children: <PerfTab campaignId={campaignId} reportId={reportId} /> },
                    { key: 'timeline', label: 'Timeline', children: <TimelineTab campaignId={campaignId} reportId={reportId} period={period} /> },
                    { key: 'platforms', label: 'Platforms', children: <PlatformsTab campaignId={campaignId} reportId={reportId} /> },
                    { key: 'content-types', label: 'Content Types', children: <ContentTypesTab campaignId={campaignId} reportId={reportId} /> },
                    { key: 'heatmap', label: 'Heatmap', children: <HeatmapTab campaignId={campaignId} reportId={reportId} /> },
                ]}
            />
        </div>
    );
};

const ReportSummaryTab: React.FC<{ campaignId?: number; reportId?: string; period: { start?: string | null; end?: string | null } }> = ({ campaignId, reportId, period }) => {
    const { data, isLoading } = useReportSummary(campaignId, reportId);
    const avatars = useCampaignAvatarMap(campaignId);
    const { data: top = [] } = useReportTopPosts(campaignId, reportId);
    const { data: influencers = [] } = useReportInfluencers(campaignId, reportId);

    const boards = [
        { title: 'MOST ACTIVE INFLUENCERS', key: 'posts' as const },
        { title: 'MOST ENGAGING INFLUENCERS', key: 'engagements' as const },
        { title: 'HIGHEST ENG RATE', key: 'engagement_rate' as const },
        { title: 'MOST VIDEO VIEWS', key: 'video_views' as const },
    ];

    const ranked = (key: typeof boards[number]['key']) =>
        [...influencers].sort((a, b) => (b[key] as number) - (a[key] as number)).slice(0, 5);

    const fmt = (key: typeof boards[number]['key'], v: number) => {
        if (key === 'engagement_rate') return formatPct(v);
        if (key === 'posts') return String(v);
        return formatCompact(v);
    };

    return (
        <div>
            <SummarySection data={data} loading={isLoading} skipFetch period={period} />
            <Row gutter={12} style={{ marginTop: 16 }}>
                {boards.map((b) => {
                    const list = ranked(b.key);
                    const max = Math.max(1, ...list.map((x) => x[b.key] as number));
                    return (
                        <Col span={6} key={b.key}>
                            <Card size="small" title={b.title}>
                                {list.map((row) => (
                                    <div key={row.creator_id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <Avatar src={avatars[row.creator_id] ? `data:image/jpeg;base64,${avatars[row.creator_id]}` : undefined} size={28}>{row.name[0]}</Avatar>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Typography.Text ellipsis style={{ fontSize: 12 }}>{row.name}</Typography.Text>
                                            <Progress percent={(Number(row[b.key]) / max) * 100} showInfo={false} size="small" />
                                        </div>
                                        <Typography.Text style={{ fontSize: 12 }}>{fmt(b.key, row[b.key] as number)}</Typography.Text>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            <Typography.Title level={5} style={{ marginTop: 24 }}>Top posts</Typography.Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                {top.map((p) => <PostCard key={p.id} post={p} avatar={avatars[p.creator_id]} />)}
            </div>
        </div>
    );
};

const LeaderboardTab: React.FC<{ campaignId?: number; reportId?: string }> = ({ campaignId, reportId }) => {
    const { data, isLoading } = useReportLeaderboard(campaignId, reportId);
    if (isLoading) return <Spin />;
    if (!data) return <Empty />;
    const max = Math.max(1, data.engagements, ...data.platforms.map((p) => p.engagements));
    return (
        <Card size="small" title="Top Performers">
            <Table
                pagination={false}
                dataSource={[
                    { key: 'parent', name: data.name, influencers: data.influencers, posts: data.posts, engagements: data.engagements, video_views: data.video_views, engagement_rate: data.engagement_rate, isParent: true },
                    ...data.platforms.map((p) => ({ key: p.platform, name: p.platform, ...p, isParent: false })),
                ]}
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        render: (n: string, row: any) => row.isParent ? <Typography.Text strong>{n}</Typography.Text> : n,
                    },
                    { title: "INF'S", dataIndex: 'influencers' },
                    { title: 'POSTS', dataIndex: 'posts' },
                    {
                        title: "ENG'S",
                        dataIndex: 'engagements',
                        render: (v: number, row: any) => (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-block', width: 120, height: 8, background: '#f5f5f5', borderRadius: 4 }}>
                                    <span style={{
                                        display: 'block',
                                        height: '100%',
                                        background: row.isParent ? '#1677ff' : platformColor(row.name),
                                        width: `${(v / max) * 100}%`,
                                        minWidth: v ? 4 : 0,
                                        borderRadius: 4,
                                    }} />
                                </span>
                                {formatCompact(v)}
                            </span>
                        ),
                    },
                    { title: 'VIDEO VIEWS', dataIndex: 'video_views', render: (v: number) => formatCompact(v) },
                    { title: 'ENG RATE', dataIndex: 'engagement_rate', render: (v: number) => formatPct(v) },
                ]}
                summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{data.influencers}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{data.posts}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{formatCompact(data.engagements)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{formatCompact(data.video_views)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{formatPct(data.engagement_rate)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />
        </Card>
    );
};

const PerfTab: React.FC<{ campaignId?: number; reportId?: string }> = ({ campaignId, reportId }) => {
    const { data: board } = useReportLeaderboard(campaignId, reportId);
    const avatars = useCampaignAvatarMap(campaignId);
    const { data: influencers = [] } = useReportInfluencers(campaignId, reportId);
    const [sort, setSort] = React.useState<'posts' | 'engagements' | 'video_views'>('posts');
    const rows = [...influencers].sort((a, b) => (b[sort] as number) - (a[sort] as number));
    const platforms = board?.platforms ?? [];
    return (
        <div>
            <Card size="small" title="Top Performers" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    {([
                        { label: 'ACTIVATED INFLUENCERS', field: 'influencers' as const, total: board?.influencers ?? 0 },
                        { label: 'POSTS', field: 'posts' as const, total: board?.posts ?? 0 },
                        { label: 'ENGAGEMENTS', field: 'engagements' as const, total: board?.engagements ?? 0 },
                    ]).map((b) => {
                        const max = Math.max(1, ...platforms.map((p) => p[b.field] as number));
                        return (
                            <Col span={8} key={b.field}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.label}</div>
                                <Typography.Text strong>{b.field === 'engagements' ? formatCompact(b.total) : b.total}</Typography.Text>
                                {platforms.map((p) => (
                                    <div key={p.platform} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                        <div style={{ width: 72, fontSize: 12 }}>{p.platform}</div>
                                        <div style={{ flex: 1, height: 10, background: '#f5f5f5' }}>
                                            <div style={{
                                                width: `${((p[b.field] as number) / max) * 100}%`,
                                                height: '100%',
                                                background: platformColor(p.platform),
                                            }} />
                                        </div>
                                        <span style={{ fontSize: 12 }}>{b.field === 'engagements' ? formatCompact(p[b.field]) : p[b.field]}</span>
                                    </div>
                                ))}
                            </Col>
                        );
                    })}
                </Row>
            </Card>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Select
                    size="small"
                    value={sort}
                    onChange={setSort}
                    options={[
                        { label: 'Most Active', value: 'posts' },
                        { label: 'Most Engaging', value: 'engagements' },
                        { label: 'Most Views', value: 'video_views' },
                    ]}
                />
            </div>
            <Table
                rowKey="creator_id"
                dataSource={rows}
                pagination={false}
                columns={[
                    {
                        title: 'NAME',
                        dataIndex: 'name',
                        render: (n: string, row) => (
                            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                <Avatar src={avatars[row.creator_id] ? `data:image/jpeg;base64,${avatars[row.creator_id]}` : undefined} size={28}>{n[0]}</Avatar>
                                {n}
                            </span>
                        ),
                    },
                    { title: 'TIER', dataIndex: 'tier', render: (t: string) => t ? t[0].toUpperCase() + t.slice(1) : '--' },
                    { title: 'AUDIENCE', dataIndex: 'audience', render: (v: number) => formatCompact(v) },
                    { title: 'POSTS', dataIndex: 'posts' },
                    { title: "ENG'S", dataIndex: 'engagements', render: (v: number) => formatCompact(v) },
                    { title: 'VIDEO VIEWS', dataIndex: 'video_views', render: (v: number) => formatCompact(v) },
                    { title: 'ENG RATE', dataIndex: 'engagement_rate', render: (v: number) => formatPct(v) },
                ]}
            />
        </div>
    );
};

const TimelineTab: React.FC<{ campaignId?: number; reportId?: string; period: { start?: string | null; end?: string | null } }> = ({ campaignId, reportId, period }) => {
    const { data, isLoading } = useReportSummary(campaignId, reportId);
    const [metric, setMetric] = React.useState<'posts' | 'vit'>('posts');
    if (isLoading) return <Spin />;
    const chartData = dailyChartSeries(data?.daily, metric, period.start, period.end);
    return (
        <Card
            size="small"
            title="Performance Over Time"
            extra={
                <Select size="small" value={metric} onChange={setMetric} options={[{ label: 'Posts', value: 'posts' }, { label: 'VIT', value: 'vit' }]} />
            }
        >
            {chartData.length === 0 ? <Empty /> : (
                <Line
                    data={chartData}
                    xField="date"
                    yField="value"
                    height={280}
                    autoFit={false}
                    smooth={false}
                    color="#1677ff"
                    point={{ size: 0, style: { opacity: 0 } }}
                    yAxis={{ min: 0 }}
                    xAxis={{
                        tickCount: 9,
                        label: {
                            autoHide: true,
                            autoRotate: false,
                            formatter: (value: string) => dayAxisLabel(value),
                        },
                    }}
                />
            )}
        </Card>
    );
};

const PlatformsTab: React.FC<{ campaignId?: number; reportId?: string }> = ({ campaignId, reportId }) => {
    const { data } = useReportLeaderboard(campaignId, reportId);
    if (!data) return <Spin />;
    const cols = [
        { label: 'ACTIVATED INFLUENCERS', field: 'influencers' as const },
        { label: 'POSTS', field: 'posts' as const },
        { label: 'ENGAGEMENTS', field: 'engagements' as const },
    ];
    return (
        <Card size="small" title="Platform Breakdown">
            <Row gutter={16}>
                {cols.map((c) => {
                    const max = Math.max(1, ...data.platforms.map((p) => p[c.field] as number));
                    return (
                        <Col span={8} key={c.field}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{c.label}</div>
                            {data.platforms.map((p) => (
                                    <div key={p.platform} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <div style={{ width: 80 }}>{p.platform}</div>
                                        <div style={{ flex: 1, height: 14, background: '#f5f5f5' }}>
                                            <div style={{
                                                width: `${((p[c.field] as number) / max) * 100}%`,
                                                height: '100%',
                                                background: platformColor(p.platform),
                                            }} />
                                        </div>
                                        <span>{c.field === 'engagements' ? formatCompact(p[c.field]) : p[c.field]}</span>
                                    </div>
                            ))}
                        </Col>
                    );
                })}
            </Row>
        </Card>
    );
};

const ContentTypesTab: React.FC<{ campaignId?: number; reportId?: string }> = ({ campaignId, reportId }) => {
    const { data } = useReportContentTypes(campaignId, reportId);
    const { data: influencers = [] } = useReportInfluencers(campaignId, reportId);
    if (!data) return <Spin />;
    const labels: Record<string, string> = { story: 'Story Content', feed: 'Feed Content', stream: 'Stream Content' };
    const maxCount = Math.max(1, ...data.buckets.map((b) => b.count || 0));
    const maxInf = Math.max(1, ...data.buckets.map((b) => b.value || 0));
    return (
        <div>
            <Card size="small" title="Content Type Breakdown" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>ACTIVATED INFLUENCERS</div>
                        {data.buckets.map((b) => (
                            <BarRow key={b.name} label={labels[b.name] || b.name} value={b.value} max={maxInf} color="#111" />
                        ))}
                    </Col>
                    <Col span={8}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>POSTS</div>
                        {data.buckets.map((b) => (
                            <BarRow key={b.name} label={labels[b.name] || b.name} value={b.count || 0} max={maxCount} color="#111" />
                        ))}
                    </Col>
                </Row>
            </Card>
            <Table
                rowKey="creator_id"
                dataSource={influencers}
                pagination={false}
                columns={[
                    { title: 'NAME', dataIndex: 'name' },
                    { title: 'TIER', dataIndex: 'tier' },
                    { title: 'AUDIENCE', dataIndex: 'audience', render: (v: number) => formatCompact(v) },
                    { title: 'POSTS', dataIndex: 'posts' },
                    { title: "ENG'S", dataIndex: 'engagements', render: (v: number) => formatCompact(v) },
                    { title: 'VIDEO VIEWS', dataIndex: 'video_views', render: (v: number) => formatCompact(v) },
                    { title: 'ENG RATE', dataIndex: 'engagement_rate', render: (v: number) => formatPct(v) },
                ]}
            />
        </div>
    );
};

const BarRow: React.FC<{ label: string; value: number; max: number; color: string }> = ({ label, value, max, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 110, fontSize: 12 }}>{label}</div>
        <div style={{ flex: 1, height: 16, background: '#f5f5f5' }}>
            <div style={{ width: `${(value / max) * 100}%`, height: '100%', background: color }} />
        </div>
        <span>{value}</span>
    </div>
);

const HeatmapTab: React.FC<{ campaignId?: number; reportId?: string }> = ({ campaignId, reportId }) => {
    const { data, isLoading } = useReportHeatmap(campaignId, reportId);
    const avatars = useCampaignAvatarMap(campaignId);
    if (isLoading || !data) return <Spin />;
    const plats = ['ig', 'yt', 'tt'] as const;
    const max = Math.max(1, ...data.creators.flatMap((r) => plats.map((p) => r.counts?.[p] ?? 0)), data.totals.total);
    const cell = (n: number) => {
        if (!n) return {};
        const t = n / max;
        return { background: t > 0.6 ? '#ff4d4f' : t > 0.3 ? '#69b1ff' : '#bae0ff', textAlign: 'center' as const, fontWeight: 600 };
    };
    return (
        <Card size="small" title="Heatmap of Posts by Platforms">
            <Table
                pagination={false}
                rowKey={(r) => String(r.creator_id ?? r.name)}
                dataSource={[...data.creators, { ...data.totals, creator_id: 0 }]}
                columns={[
                    {
                        title: 'INFLUENCERS',
                        dataIndex: 'name',
                        render: (n: string, row) => row.creator_id ? (
                            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                <Avatar src={avatars[row.creator_id ?? 0] ? `data:image/jpeg;base64,${avatars[row.creator_id ?? 0]}` : undefined} size={24}>{n[0]}</Avatar>
                                {n}
                            </span>
                        ) : <Typography.Text strong>{n}</Typography.Text>,
                    },
                    ...plats.map((p) => ({
                        title: PLATFORM_LABEL[p].toUpperCase(),
                        render: (_: unknown, row: typeof data.creators[0]) => (
                            <div style={cell(row.counts?.[p] ?? 0)}>{row.counts?.[p] || ''}</div>
                        ),
                    })),
                    {
                        title: 'TOTAL',
                        dataIndex: 'total',
                        render: (n: number) => <div style={{ ...cell(n), background: n ? '#ff7875' : undefined }}>{n || ''}</div>,
                    },
                ]}
            />
        </Card>
    );
};

export default ReportView;
